import { useTranslation } from "react-i18next";
import {
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  Filter,
  GridComponent,
  Inject,
  Page,
  PdfExport,
} from "@syncfusion/ej2-react-grids";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import { format } from "date-fns";
import { Suspense, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FaCarAlt, FaRegFileExcel, FaRegFilePdf } from "react-icons/fa";
import { useTheme } from "../../../contexts/ThemeContext";
import { vehicleService } from "../../../Services/vehicle.service";
import VisitsMapComponent from "../../Components/Maps/VisitsMapComponent";
import Spinner from "../../Components/Spinner";

// Syncfusion CSS imports
import "@syncfusion/ej2-base/styles/bootstrap5.css";
import "@syncfusion/ej2-buttons/styles/bootstrap5.css";
import "@syncfusion/ej2-dropdowns/styles/bootstrap5.css";
import "@syncfusion/ej2-grids/styles/bootstrap5.css";
import "@syncfusion/ej2-inputs/styles/bootstrap5.css";
import "@syncfusion/ej2-navigations/styles/bootstrap5.css";
import "@syncfusion/ej2-popups/styles/bootstrap5.css";
import "@syncfusion/ej2-splitbuttons/styles/bootstrap5.css";
import { getLocationCoordinates } from "../../../Services/locations.service";
import { reportService } from "../../../Services/report.service";
import { downloadService } from "../../../Services/download.service";

// Estilo customizado para o grid na modal
const gridModalStyles = `
  #modalVehiclesGrid .e-gridpager {
    padding: 4px 12px !important;
    min-height: 36px !important;
    height: 36px !important;
    border-top: 1px solid rgba(128, 128, 128, 0.2) !important;
  }
  
  #modalVehiclesGrid .e-gridcontent {
    overflow-y: hidden !important;
    overflow-x: auto !important;
  }
  
  #modalVehiclesGrid .e-row {
    height: 32px !important;
    min-height: 32px !important;
  }
  
  #modalVehiclesGrid .e-rowcell {
    padding: 4px 8px !important;
    line-height: 24px !important;
    font-size: 13px !important;
  }
  
  #modalVehiclesGrid .e-headercelldiv {
    padding: 4px 6px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.2 !important;
    font-size: 12px !important;
    font-weight: 600 !important;
  }
  
  #modalVehiclesGrid .e-headercell {
    height: auto !important;
    min-height: 36px !important;
  }
  
  #modalVehiclesGrid .e-gridheader {
    overflow: visible !important;
  }
  
  #modalVehiclesGrid .e-table {
    width: 100% !important;
  }
`;


export default function ModalVisits(props) {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  const { theme } = useTheme();

  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("grid"); // controle das abas
  const [mapLoading] = useState(false);
  const [createMap] = useState(true);

  const handleClose = () => props.handleClose(!props.show);

  const handleClickPdfExport = () => {
    getReportFile("pdf");
  };

  const handleClickExcelExport = () => {
    getReportFile("xlsx");
  };

  const getReportFile = async (reportType) => {
    const vehicleId = props.dataVisits?.vehicleId;
    const payload = {
      latitude: props.latitude,
      longitude: props.longitude,
      reportType,
    };

    try {
      const response = await reportService.fetchNearbyVehiclesReport(
        vehicleId,
        payload
      );
      downloadService.downloadBase64File(
        response,
        "pdf",
        "NearbyVehiclesReport"
      );
    } catch (error) {
      console.error("Erro ao gerar o relatório:", error);
    }
  };

  // 1) Busca única usando as coordenadas
  useEffect(() => {
    const fetchData = async () => {
      if (!props.show) return;

      const lat = Number(props.latitude);
      const lng = Number(props.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        setData([]);
        return;
      }

      setDataLoading(true);
      try {
        const response = await vehicleService.fetchNearbyVehicles(lat, lng);
        setData(Array.isArray(response) ? response : []);
      } catch {
        setData([]);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [props.show, props.latitude, props.longitude]);

  // 2) Calcula o pageSize dinamicamente baseado na altura disponível
  const calculatePageSize = () => {
    if (!props.show || activeTab !== "grid") return;

    const windowHeight = window.innerHeight;
    const modalHeaderHeight = headerRef.current?.offsetHeight ?? 55; // Header da modal
    const tabsHeight = 32; // Altura das tabs
    const buttonsHeight = 36; // Altura dos botões de exportação
    const gridHeaderHeight = 36; // Cabeçalho do grid (pode quebrar linha)
    const paginationHeight = 36; // Altura da paginação (reduzido)
    const modalPadding = 24; // Padding geral da modal (12px top + 12px bottom)
    const rowHeight = 32; // Altura de cada linha do grid
    const extraMargin = 14; // Margem extra entre componentes (tabs + botões margin)

    const totalFixedHeight =
      modalHeaderHeight +
      tabsHeight +
      buttonsHeight +
      gridHeaderHeight +
      paginationHeight +
      modalPadding +
      extraMargin;

    const availableHeight = windowHeight - totalFixedHeight;
    const calculatedPageSize = Math.max(
      Math.floor(availableHeight / rowHeight),
      5
    );

    console.log("📊 Cálculo de PageSize:", {
      windowHeight,
      totalFixedHeight,
      availableHeight,
      rowHeight,
      calculatedPageSize,
      "Itens que cabem": calculatedPageSize,
    });

    setPageSize(calculatedPageSize);
  };

  // 3) Recalcula quando o modal abre, muda de tab ou redimensiona
  useEffect(() => {
    if (!props.show) return;

    // Calcula imediatamente e depois de um delay para garantir que o layout estabilizou
    calculatePageSize();
    const timer = setTimeout(calculatePageSize, 300);

    // Recalcula ao redimensionar a janela
    window.addEventListener("resize", calculatePageSize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", calculatePageSize);
    };
  }, [props.show, activeTab]);

  return (
    <>
      <style>{gridModalStyles}</style>
      <Modal
        id="modalVehicles"
        show={props.show}
        backdrop="static"
        size="lg"
        className="card-modal"
        fullscreen={true} // se quiser responsivo: "md-down"
        onHide={handleClose}
        centered
      >
        <Modal.Header ref={headerRef} closeButton>
          <Modal.Title>
            {t("TITLE.NEARBY_VEHICLES")} <FaCarAlt />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          ref={bodyRef}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 60px)", // Altura total menos o header
            paddingBottom: 0,
            padding: "12px",
            overflow: "hidden",
          }}
        >
          {/* Tabs customizadas com design moderno */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              padding: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              maxWidth: "400px",
              margin: "0 auto 0 auto",
            }}
          >
            <button
              onClick={() => setActiveTab("grid")}
              style={{
                padding: "8px 20px",
                border: "none",
                borderRadius: "8px",
                backgroundColor:
                  activeTab === "grid" ? "var(--color-font-filter-primary)" : "transparent",
                color: activeTab === "grid" ? "white" : "#6c757d",
                fontWeight: activeTab === "grid" ? "600" : "500",
                fontSize: "13px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                boxShadow:
                  activeTab === "grid"
                    ? "0 2px 8px rgba(108, 92, 231, 0.3)"
                    : "none",
                minWidth: "120px",
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "grid") {
                  e.target.style.backgroundColor = "#e9ecef";
                  e.target.style.color = "#495057";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "grid") {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#6c757d";
                }
              }}
            >
              <FaCarAlt size={14} />
              {t("VIEW_MAP_FILTERS.VEHICLES")}
            </button>
            <button
              onClick={() => setActiveTab("mapVisits")}
              style={{
                padding: "8px 20px",
                border: "none",
                borderRadius: "8px",
                backgroundColor:
                  activeTab === "mapVisits" ? "var(--color-font-filter-primary)" : "transparent",
                color: activeTab === "mapVisits" ? "white" : "#6c757d",
                fontWeight: activeTab === "mapVisits" ? "600" : "500",
                fontSize: "13px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                boxShadow:
                  activeTab === "mapVisits"
                    ? "0 2px 8px rgba(108, 92, 231, 0.3)"
                    : "none",
                minWidth: "120px",
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "mapVisits") {
                  e.target.style.backgroundColor = "#e9ecef";
                  e.target.style.color = "#495057";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "mapVisits") {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#6c757d";
                }
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              {t("TITLE.MAP")}
            </button>
          </div>

          <div
            className="tab-content"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {activeTab === "grid" && (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <div
                  className="tab-content-items"
                  style={{
                    display: "flex",
                    gap: 8,
                    flexShrink: 0,
                    padding: "8px 12px",
                    borderRadius: "8px",
                  }}
                >
                  <button
                    className="btn btn-primary"
                    onClick={handleClickPdfExport}
                  >
                    <FaRegFilePdf style={{ marginRight: 4 }} />
                    PDF
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleClickExcelExport}
                  >
                    <FaRegFileExcel style={{ marginRight: 4 }} />
                    Excel
                  </button>
                </div>

                {dataLoading ? (
                  <div className="d-flex justify-content-center my-5">
                    <Spinner />
                  </div>
                ) : (
                  <div
                    id="modalVehiclesGrid"
                    style={{
                      borderRadius: "8px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      backgroundColor: theme === "dark" ? "#2b3035" : "#ffffff",
                    }}
                  >
                    <GridComponent
                      ref={gridRef}
                      dataSource={data}
                      allowPaging
                      allowFiltering={true}
                      filterSettings={{ type: "Menu" }}
                      pageSettings={{ pageSize, pageSizes: false }}
                      allowExcelExport
                      allowPdfExport
                      rowHeight={32}
                      locale="pt-BR"
                      dataBound={calculatePageSize}
                      rowDataBound={(args) => {
                        args.row.style.cursor = "pointer";
                      }}
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="distance"
                          clipMode="EllipsisWithTooltip"
                          headerText="Distância"
                          width="7%"
                          textAlign="Left"
                          template={(props) => (
                            <span
                              style={{ color: "black" }}
                            >{`${props.distance} km`}</span>
                          )}
                        />
                        <ColumnDirective
                          field="plate"
                          headerText={t("COMMON.PLATE")}
                          width="10%"
                          textAlign="Left"
                          template={(props) => (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                            >
                              <TooltipComponent
                                content={props.plateTip}
                                position="TopCenter"
                              >
                                <span>{props.plate}</span>
                              </TooltipComponent>
                            </div>
                          )}
                        />
                        <ColumnDirective
                          field="vehicleGroupName"
                          clipMode="EllipsisWithTooltip"
                          headerText="Grupo"
                          width="10%"
                          textAlign="Left"
                        />
                        <ColumnDirective
                          field="generationDate"
                          clipMode="EllipsisWithTooltip"
                          headerText="Data/Hora"
                          width="15%"
                          textAlign="Left"
                          template={(props) =>
                            props.generationDate
                              ? format(
                                  new Date(props.generationDate),
                                  "dd/MM/yyyy HH:mm:ss"
                                )
                              : ""
                          }
                        />
                        <ColumnDirective
                          field="speedStatus"
                          clipMode="EllipsisWithTooltip"
                          headerText="ST"
                          width="7%"
                          textAlign="Center"
                          template={(props) => (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                            >
                              <TooltipComponent
                                content={
                                  props.speedStatusDescription
                                    ? props.speedStatusDescription
                                    : "Sem informação"
                                }
                                position="TopCenter"
                              >
                                <div
                                  style={{
                                    width: "15px",
                                    height: "15px",
                                    backgroundColor: props.color,
                                    margin: "0 auto",
                                  }}
                                />
                              </TooltipComponent>
                            </div>
                          )}
                        />
                        <ColumnDirective
                          field="ignitionStatus"
                          headerText="IG"
                          width="7%"
                          textAlign="Center"
                          template={(props) =>
                            props.ignitionStatus === "Ligado" ? (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <TooltipComponent
                                  content="Ligado"
                                  position="TopCenter"
                                >
                                  <img
                                    src="/images/icons/opened.png"
                                    alt="Ligado"
                                    style={{
                                      width: "24px",
                                      marginLeft: "0px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </TooltipComponent>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                }}
                              >
                                <TooltipComponent
                                  content="Desligado"
                                  position="TopCenter"
                                >
                                  <img
                                    src="/images/icons/closed.png"
                                    alt="Desligado"
                                    style={{
                                      width: "24px",
                                      marginLeft: "0px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </TooltipComponent>
                              </div>
                            )
                          }
                        />
                        <ColumnDirective
                          field="speed"
                          clipMode="EllipsisWithTooltip"
                          headerText="VE"
                          width="10%"
                          textAlign="Left"
                          template={(props) => (
                            <span>{props.speed.toFixed(2)} km/h</span>
                          )}
                        />
                        <ColumnDirective
                          field="location"
                          clipMode="EllipsisWithTooltip"
                          headerText="Localização"
                          textAlign="Left"
                          width="34%"
                          template={(props) => {
                            return (
                              <a
                                href={`https://maps.google.com/?q=${getLocationCoordinates(
                                  "location",
                                  props
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  color: "inherit",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                <div>{props.location}</div>
                              </a>
                            );
                          }}
                        />
                      </ColumnsDirective>
                      <Inject
                        services={[Page, ExcelExport, PdfExport, Filter]}
                      />
                    </GridComponent>
                  </div>
                )}
              </div>
            )}

            {activeTab === "mapVisits" && (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                }}
              >
                <Suspense fallback={<Spinner />}>
                  <VisitsMapComponent
                    id="visitsMapComponent"
                    mapLoading={mapLoading}
                    locations={data}
                    createMap={createMap}
                    dataVehicle={props.dataVisits}
                    activeTab={"mapVisits"}
                  />
                </Suspense>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}